import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    DropdownCheckIcon,
    DropdownItem,
    Input,
    Modal,
    Search,
    SparkModalPortal,
    Text,
} from '@spark';
import { sanitizeProjectName } from './projectNameUtils';
import { renderMoveTitle } from '@/features/tasks/utils/bulkDeleteCopy';

const BODY_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-08)',
    width: '100%',
};

const TOOLBAR_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--dimension-spacing-08)',
    width: '100%',
};

const SEARCH_WRAP_STYLE = {
    width: 300,
    flexShrink: 0,
};

const LIST_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
};

const CREATE_FIELD_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dimension-spacing-08)',
    width: '100%',
};

/**
 * Destination picker for bulk-moving tasks (Figma Projects `8012:1156` / `8022:2018`).
 * Move stays disabled until a project row is selected or a new name is typed.
 */
export const MoveTasksModal = ({
    open,
    itemCount = 0,
    destinations = [],
    itemNames = [],
    onClose,
    onMove,
    loading = false,
}) => {
    const [selectedName, setSelectedName] = useState(null);
    const [newProjectName, setNewProjectName] = useState('');
    const [filterQuery, setFilterQuery] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const createInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setSelectedName(null);
            setNewProjectName('');
            setFilterQuery('');
            setIsCreating(false);
        }
    }, [open]);

    useEffect(() => {
        if (isCreating) createInputRef.current?.focus();
    }, [isCreating]);

    const filteredDestinations = useMemo(() => {
        const query = filterQuery.trim().toLowerCase();
        if (!query) return destinations || [];
        return (destinations || []).filter((dest) => dest.name.toLowerCase().includes(query));
    }, [destinations, filterQuery]);

    const pendingName = sanitizeProjectName((newProjectName || '').trim(), { trimEnd: true });
    const destName = pendingName || selectedName;

    const handleMove = () => {
        if (!destName || loading) return;
        onMove?.(destName);
    };

    return (
        <SparkModalPortal open={open} onClose={onClose} size="slot" destroyOnClose>
            <Modal
                title={renderMoveTitle(itemNames, destName, itemCount)}
                showSubtitle={false}
                showScrollBar={false}
                showOverlay={false}
                fixedHeight={false}
                showFooterSlot
                footerSlot={
                    isCreating ? (
                        <div style={CREATE_FIELD_STYLE}>
                            <Input
                                ref={createInputRef}
                                showLabel={false}
                                showHelperText={false}
                                placeholder="Create new project name"
                                value={newProjectName}
                                onChange={(e) => {
                                    setNewProjectName(sanitizeProjectName(e.target.value));
                                    setSelectedName(null);
                                }}
                            />
                            {pendingName ? (
                                <DropdownCheckIcon className="spark-dropdown-item-check" />
                            ) : null}
                        </div>
                    ) : (
                        <Button
                            variant="ghost"
                            size="tiny"
                            type="button"
                            showStartIcon
                            onClick={() => setIsCreating(true)}
                        >
                            Add New Project
                        </Button>
                    )
                }
                primaryLabel="Move"
                tertiaryLabel="Cancel"
                primaryDisabled={!destName || loading}
                primaryDisableWhenReadOnly
                onPrimaryClick={handleMove}
                onTertiaryClick={onClose}
                onCloseClick={onClose}
            >
                <div style={BODY_STYLE}>
                    <div style={TOOLBAR_STYLE}>
                        <Text variant="base-semibold" color="primary">
                            Select a destination for these file(s)
                        </Text>
                        <div style={SEARCH_WRAP_STYLE}>
                            <Search
                                size="small"
                                placeholder="Search projects"
                                value={filterQuery}
                                onChange={(e) => setFilterQuery(e.target.value)}
                                onClear={() => setFilterQuery('')}
                            />
                        </div>
                    </div>
                    <div style={LIST_STYLE} role="menu" aria-label="Projects">
                        {filteredDestinations.length > 0 ? (
                            filteredDestinations.map((dest) => (
                                <DropdownItem
                                    key={dest.name}
                                    label={`${dest.name} (${dest.count})`}
                                    state={!pendingName && dest.name === selectedName ? 'selected' : 'default'}
                                    showPrefixIcon
                                    prefixIconSlug="library"
                                    onClick={() => {
                                        setSelectedName(dest.name);
                                        setNewProjectName('');
                                        setIsCreating(false);
                                    }}
                                />
                            ))
                        ) : (
                            <Text variant="base-regular" color="secondary" as="p" role="status">
                                No matching projects
                            </Text>
                        )}
                    </div>
                </div>
            </Modal>
        </SparkModalPortal>
    );
};

export default MoveTasksModal;
